import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { LEVEL_OPTIONS, MENS_APPARATUS, WOMENS_APPARATUS } from '../../constants/competitionConstants';

const defaultJudges = [
  { id: 1, name: 'Judge Slot 1', role: 'Head Judge', apparatus: 'Floor', certification: 'TBD' },
  { id: 2, name: 'Judge Slot 2', role: 'D-Panel', apparatus: 'Pommel Horse', certification: 'TBD' },
  { id: 3, name: 'Judge Slot 3', role: 'E-Panel', apparatus: 'Still Rings', certification: 'TBD' },
  { id: 4, name: 'Judge Slot 4', role: 'D-Panel', apparatus: 'Vault', certification: 'TBD' },
  { id: 5, name: 'Judge Slot 5', role: 'E-Panel', apparatus: 'Parallel Bars', certification: 'TBD' },
  { id: 6, name: 'Judge Slot 6', role: 'Head Judge', apparatus: 'High Bar', certification: 'TBD' }
];

const CompetitionJudging = ({ judges = defaultJudges, scoringRules = [], levels = [], sessions = [], competitionColors, gender }) => {
  const theme = useTheme();

  // Use provided gender prop, or infer from sessions as fallback
  const sessionGenders = new Set(sessions.map(s => s.gender).filter(Boolean));
  const inferredFromSessions = sessionGenders.size === 1 ? [...sessionGenders][0] : null;
  const inferredGender = gender || inferredFromSessions;

  // Gather levels from props plus any session-defined levels
  const levelInputs = [
    ...(levels || []),
    ...((sessions || []).flatMap(s => s.levels || []))
  ].map(l => typeof l === 'object' ? (l.id || l.value || l.label) : l)
   .filter(Boolean);

  // Filter levels to only include those applicable to this competition
  const applicableLevels = LEVEL_OPTIONS.filter(option => {
    // If we have a clear inferred gender, filter out non-matching genders
    if (inferredGender && option.gender !== inferredGender) {
      return false;
    }

    return levelInputs.some(l => {
      const normalizedLevelId = String(l).trim();
      
      return normalizedLevelId === option.label || 
             normalizedLevelId === option.value || 
             (option.aliases && option.aliases.includes(normalizedLevelId));
    });
  });

  const placeholderRules = scoringRules.length > 0 ? scoringRules : [
    { category: 'Difficulty (D)', description: 'Base start values by level and apparatus', maxScore: 'Varies', deductions: 'See FIG/USAG guidelines' },
    { category: 'Execution (E)', description: 'Form, amplitude, artistry', maxScore: '10.0', deductions: '0.1 - 0.5 per error' },
    { category: 'Neutral', description: 'Time, line, behavior, attire', maxScore: '—', deductions: '0.1 - 1.0 (neutral)' },
  ];

  const apparatusList = inferredGender === 'F' ? WOMENS_APPARATUS : MENS_APPARATUS;
  const apparatusCount = apparatusList.length;
  const renderedJudges = judges.length > 0 ? judges : Array.from({ length: apparatusCount }, (_, idx) => ({
    id: idx + 1,
    name: `Placeholder Judge ${idx + 1}`,
    role: 'Head Judge',
    apparatus: apparatusList[idx]?.name || `Apparatus ${idx + 1}`,
    certification: 'TBD'
  }));

  const numericLevelFallback = Array.from({ length: 8 }, (_, i) => {
    const levelNum = i + 3; // Levels 3-10
    return {
      label: `Level ${levelNum}`,
      value: `L${levelNum}`,
      gender: inferredGender || 'M'
    };
  });

  // If we have numeric-looking levels in inputs but no applicableLevel match, build from those
  const detectedNumericLevels = levelInputs
    .map(l => parseInt(String(l).replace(/\D/g, ''), 10))
    .filter(n => n >= 3 && n <= 10);
  const derivedNumericFallback = detectedNumericLevels.length > 0
    ? Array.from(new Set(detectedNumericLevels)).sort((a, b) => a - b).map(n => ({
      label: `Level ${n}`,
      value: `L${n}`,
      gender: inferredGender || 'M'
    }))
    : null;

  const fallbackLevels = derivedNumericFallback || numericLevelFallback;

  const scoringLevels = applicableLevels.length > 0 ? applicableLevels : fallbackLevels;

  return (
    <Grid container spacing={3} sx={{ width: '100%' }} data-ui="judges-content">
      <Grid item xs={12} sx={{ width: '100%', maxWidth: 'none' }}>
        <Card sx={{ maxWidth: 'none' }} data-ui="judges-panel-card">
          <CardContent sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>Judges Panel</Typography>

            {/* Show note when all judges are placeholders */}
            {renderedJudges.every(judge => judge.name.includes('Placeholder')) && (
              <Box sx={{
                mb: 2,
                p: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(theme.palette.info.main, 0.25)
              }}
              data-ui="judges-placeholder-notice"
              >
                <Typography variant="body2" color="text.primary">
                  <strong>Placeholder:</strong> No judges have been added yet. {apparatusCount} placeholder judges are shown to mirror the apparatus—replace them with your actual assignments.
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ width: '100%' }}>
              <Table data-ui="judges-panel-table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: `${competitionColors.primary}22` }}>
<TableCell>Name</TableCell>
<TableCell>Role</TableCell>
<TableCell>Apparatus</TableCell>
<TableCell>Certification</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderedJudges.map((judge) => (
                    <TableRow key={judge.id}>
<TableCell>{judge.name}</TableCell>
<TableCell>{judge.role}</TableCell>
<TableCell>{judge.apparatus}</TableCell>
<TableCell>{judge.certification}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sx={{ width: '100%', maxWidth: 'none' }}>
        <Card sx={{ maxWidth: 'none' }} data-ui="judges-scoring-rules-card">
          <CardContent sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>Scoring Rules</Typography>
            <TableContainer sx={{ width: '100%' }}>
              <Table data-ui="judges-scoring-rules-table" size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: `${competitionColors.primary}22` }}>
<TableCell>Level</TableCell>
<TableCell>Type</TableCell>
<TableCell>Start Value</TableCell>
<TableCell>Description</TableCell>
<TableCell>Deductions</TableCell>
<TableCell align="center" sx={{ width: 50 }}>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicableLevels.length > 0 ? (
                    applicableLevels.map((level) => (
                      <TableRow key={`${level.gender}-${level.value}`}>
<TableCell><strong>{level.label}</strong></TableCell>
<TableCell>{level.scoring?.type || 'Optional'}</TableCell>
<TableCell>{level.scoring?.startValue || '10.0'}</TableCell>
<TableCell sx={{ maxWidth: 300 }}>{level.scoring?.description || 'Standard scoring rules apply'}</TableCell>
<TableCell sx={{ maxWidth: 250 }}>{level.scoring?.deductions || '0.05-0.50 per error'}</TableCell>
<TableCell align="center">
  {level.scoring?.sourceUrl ? (
    <Tooltip title="View official USAG rules">
      <IconButton
        size="small"
        component="a"
        href={level.scoring.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : '—'}
</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    placeholderRules.map((rule, idx) => (
                      <TableRow key={`rule-${idx}`}>
                        <TableCell>{rule.category}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>{rule.maxScore}</TableCell>
                        <TableCell>{rule.description}</TableCell>
                        <TableCell>{rule.deductions}</TableCell>
                        <TableCell>—</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Disclaimer and official sources */}
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: alpha(theme.palette.warning.main, 0.08), borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Note:</strong> Scoring summaries are provided for quick reference only.
                Always consult the official Code of Points for complete rules:{' '}
                <Link href="https://usagym.org/men/rules/" target="_blank" rel="noopener noreferrer">
                  Men's JO
                </Link>
                {' • '}
                <Link href="https://usagym.org/women/xcel/" target="_blank" rel="noopener noreferrer">
                  Women's Xcel
                </Link>
                {' • '}
                <Link href="https://usagym.org/women/rules/" target="_blank" rel="noopener noreferrer">
                  Women's Compulsory
                </Link>
                {' • '}
                <Link href="https://usagym.org/women/development/optionals/" target="_blank" rel="noopener noreferrer">
                  Women's Optional
                </Link>
                {' • '}
                <Link href="https://www.gymnastics.sport/site/rules/" target="_blank" rel="noopener noreferrer">
                  FIG Elite
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>


    </Grid>
  );
};

export default CompetitionJudging;
